// @flow

import React, { Component } from 'react';
import color from 'color';
import * as cssUtils from 'app/utils/cssUtils';
import useSheet from 'app/jss';
import Avatar from 'app/components/commons/Avatar';
import Button from './Button';
import Container from '../layout/Container';
import FormValidationMessage from './FormValidationMessage';
import ImageUploader from './ImageUploader';
import ProgressButton from './ProgressButton';
import ReactCropper from './ReactCropper';
import { LARGE } from '../../constants/avatarSizeTypes';

// Renders an upload image button + a grid of default avatars.
// will also render the avatar editor component once the
// upload image button is clicked.

type Props = {
  classes: Object,
  currentAvatar?: Object,
  errors?: Array<Object>,
  onClose: () => void,
  onRemove: () => void,
  onSave: () => void,
  updatingUser?: boolean,
};

class AvatarChooser extends Component<Props> {
  props: Props;
  static defaultProps = {
    currentAvatar: undefined,
    updatingUser: false,
    errors: [],
  };

  constructor(...args) {
    super(...args);
    this.state = {
      scale: 1,
      selectedAvatar: null,
    };
    this.handleChooseAvatar = this.handleChooseAvatar.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleScale = this.handleScale.bind(this);
  }

  // selected an avatar from the file system.
  handleChooseAvatar(avatar) {
    this.setState({
      selectedAvatar: avatar,
    });
  }

  handleSave() {
    if (!this.cropperRef) {
      return;
    }

    const image = this.cropperRef.getCroppedCanvas({
      height: LARGE,
      width: LARGE,
    });
    const imageUrl = image.toDataURL();

    this.props.onSave(imageUrl, {
      x: 0,
      y: 0,
      height: image.height,
      width: image.width,
    });
  }

  handleScale(event) {
    this.setState({
      scale: parseFloat(event.target.value),
    });
  }

  render() {
    const { classes, currentAvatar, errors, onClose, onRemove, updatingUser } = this.props;
    const { scale, selectedAvatar } = this.state;
    const isDefaultAvatar = !currentAvatar || currentAvatar.small.endsWith('/avatar.jpg');

    return (
      <div className={classes.chooser}>
        {selectedAvatar ? (
          <div>
            <ReactCropper
              ref={(ref) => {
                this.cropperRef = ref;
              }}
              className={classes.cropper}
              image={selectedAvatar}
              width={LARGE}
              height={LARGE}
              scale={scale}
            />
            <input
              name="scale"
              type="range"
              value={scale}
              onChange={this.handleScale}
              min="1"
              max="2"
              step="0.01"
            />
          </div>
        ) : (
          <Container size="xlarge">
            <div className={classes.avatarContainer}>
              <Avatar avatar={currentAvatar} size="xlarge" className={classes.avatarBox} />
            </div>
            {!isDefaultAvatar &&
              !selectedAvatar && (
                <Button
                  block
                  kind="cancel"
                  onClick={onRemove}
                  className={classes.remove}
                  size="small"
                >
                  Remove Avatar
                </Button>
              )}
            <ImageUploader onChange={this.handleChooseAvatar} />
          </Container>
        )}
        {errors.length > 0 && <FormValidationMessage message={errors} />}
        {selectedAvatar && (
          <div className={classes.save}>
            <ProgressButton
              block
              defaultText="Save"
              inProgress={updatingUser}
              progressText="Saving"
              size="large"
              onClick={this.handleSave}
            />
          </div>
        )}
        <Button block kind="cancel" onClick={onClose}>
          Cancel
        </Button>
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    chooser: {
      margin: {
        right: 'auto',
        left: 'auto',
      },
      maxWidth: LARGE,
    },
    avatarContainer: {
      display: 'flex',
      justifyContent: 'center',
      opacity: 0.5,
    },
    avatarBox: {
      backgroundColor: theme.color.basis4,
    },
    cropper: {
      marginBottom: theme.spacing.small,
    },
    remove: {
      background: 'none',
      color: theme.color.danger,
      transition: 'color 0.2s',
      ...cssUtils.hover({
        color: `${color(theme.color.danger)
          .lighten(0.3)
          .string()}`,
      }),
    },
    save: {
      marginBottom: theme.spacing.small,
    },
  };
}

export default useSheet(applyStyles)(AvatarChooser);
